import { Component, OnInit } from "@angular/core";
import { forkJoin, of } from "rxjs";
import { map } from "rxjs/operators";

import { ServerApiService } from "app/services/server-api.service";

import {
  TeacherPath,
  PathCycle,
  AttendanceCycle,
  AttendanceSession,
  AttendanceMatrixData,
} from "app/models/interfaces/attendance/attendanceMatrix";

declare var $: any;

interface MatrixRow {
  studentID: string;
  firstName: string;
  familyName: string;
  cells: (number | null)[]; // one per session: 1 attended, 0 absent, null no record
  attended: number;
  percent: number;
  timesRepeated: number | null; // from coursesperstudent; null = no record (treated as first time)
}

interface PathGroup {
  pathCode: number | null; // null = students with no path record
  pathName: string; // display label, e.g. "DevOps - First time"
  rows: MatrixRow[];
  included: boolean; // checkbox state: include this group in CSV exports
  order: number; // display order: 0 = first time, 1 = repeating
}

interface CycleMatrix {
  sessions: AttendanceSession[];
  rows: MatrixRow[]; // all students, flat (used for empty checks)
  groups: PathGroup[]; // rows grouped by student path, filtered path first
  held: number; // sessions already held (sessionDate <= today)
  loading: boolean;
}

@Component({
  selector: "app-students-attendance",
  templateUrl: "./students-attendance.component.html",
  styleUrls: ["./students-attendance.component.css"],
  providers: [ServerApiService],
})
export class StudentsAttendanceComponent implements OnInit {
  // filter state
  paths: TeacherPath[] = [];
  categories: string[] = [];
  pathNames: TeacherPath[] = [];
  pathCycles: PathCycle[] = [];
  loadingPathCycles: boolean = false;
  selectedCategory: string = null;
  selectedPathCode: number = null;
  selectedPathCycleId: number = null;
  startDate: string = "";
  endDate: string = "";

  // results
  cycles: AttendanceCycle[] = [];
  filtered: boolean = false;
  loadingCycles: boolean = false;
  filteredPathCode: number = null; // path snapshotted when Filter was pressed
  filteredPathCycle: PathCycle = null; // path cycle snapshotted when Filter was pressed

  // per-cycle state, keyed by cycle code
  openCycles: { [code: number]: boolean } = {};
  matrices: { [code: number]: CycleMatrix } = {};
  exportingAll: boolean = false;

  constructor(private ServerApiService: ServerApiService) {}

  ngOnInit(): void {
    // dates start empty = no date narrowing (all cycles of the path cycle, incl. future)
    this.ServerApiService.fetchAllPaths().subscribe(
      (paths) => {
        if (Array.isArray(paths)) {
          this.paths = paths;
          this.categories = paths
            .map((p) => p.category)
            .filter((cat, i, all) => all.indexOf(cat) === i);
        }
      },
      (error) => {
        this.showNotification("bottom", "left", 4, "Could not load paths");
      }
    );
  }

  onCategoryChange() {
    this.selectedPathCode = null;
    this.selectedPathCycleId = null;
    this.pathCycles = [];
    this.pathNames = this.paths.filter(
      (p) => p.category === this.selectedCategory
    );
  }

  onPathChange() {
    this.selectedPathCycleId = null;
    this.pathCycles = [];
    if (this.selectedPathCode === null) return;
    this.loadingPathCycles = true;
    this.ServerApiService.fetchPathCycles(this.selectedPathCode).subscribe(
      (data) => {
        this.loadingPathCycles = false;
        this.pathCycles = Array.isArray(data) ? data : [];
      },
      (error) => {
        this.loadingPathCycles = false;
        this.showNotification("bottom", "left", 4, "Could not load path cycles");
      }
    );
  }

  canFilter(): boolean {
    return (
      !!this.selectedCategory &&
      this.selectedPathCode !== null &&
      this.selectedPathCycleId !== null
    );
  }

  applyFilter() {
    if (!this.canFilter()) return;
    this.filtered = true;
    this.loadingCycles = true;
    this.cycles = [];
    this.openCycles = {};
    this.matrices = {};
    this.filteredPathCode = this.selectedPathCode;
    this.filteredPathCycle =
      this.pathCycles.find((pc) => pc.cycleId === this.selectedPathCycleId) ||
      null;
    this.ServerApiService.fetchCyclesByPathCycle(
      this.selectedPathCycleId,
      this.filteredPathCode,
      this.startDate,
      this.endDate
    ).subscribe(
      (data) => {
        this.loadingCycles = false;
        this.cycles = Array.isArray(data) ? data : [];
      },
      (error) => {
        this.loadingCycles = false;
        this.showNotification("bottom", "left", 4, "Could not load cycles");
      }
    );
  }

  toggleCycle(cycle: AttendanceCycle) {
    this.openCycles[cycle.code] = !this.openCycles[cycle.code];
    if (this.openCycles[cycle.code] && !this.matrices[cycle.code]) {
      this.loadMatrix(cycle.code);
    }
  }

  openAll() {
    for (const cycle of this.cycles) {
      this.openCycles[cycle.code] = true;
      if (!this.matrices[cycle.code]) this.loadMatrix(cycle.code);
    }
  }

  closeAll() {
    this.openCycles = {};
  }

  private loadMatrix(code: number) {
    this.matrices[code] = {
      sessions: [],
      rows: [],
      groups: [],
      held: 0,
      loading: true,
    };
    this.ServerApiService.fetchAttendanceMatrix(code).subscribe(
      (data: AttendanceMatrixData) => {
        this.matrices[code] = this.buildMatrix(data);
      },
      (error) => {
        delete this.matrices[code];
        this.openCycles[code] = false;
        this.showNotification("bottom", "left", 4, "Could not load attendance");
      }
    );
  }

  // pivot sessions + visit rows into one matrix row per student
  private buildMatrix(data: AttendanceMatrixData): CycleMatrix {
    const sessions = (data && data.sessions) || [];
    const visits = (data && data.visits) || [];
    const studentPaths = (data && data.studentPaths) || [];
    const today = new Date();
    const held = sessions.filter(
      (s) => s.sessionDate && new Date(s.sessionDate) <= today
    ).length;
    const sessionIndex: { [num: number]: number } = {};
    sessions.forEach((s, i) => (sessionIndex[s.session_num] = i));

    const byStudent: { [id: string]: MatrixRow } = {};
    for (const v of visits) {
      if (!byStudent[v.studentID]) {
        byStudent[v.studentID] = {
          studentID: v.studentID,
          firstName: v.firstName || "",
          familyName: v.familyName || "",
          cells: sessions.map(() => null),
          attended: 0,
          percent: 0,
          timesRepeated:
            v.timesRepeated === undefined ? null : v.timesRepeated,
        };
      }
      const idx = sessionIndex[v.session];
      if (idx !== undefined && v.visit !== null && v.visit !== undefined) {
        byStudent[v.studentID].cells[idx] = v.visit;
      }
    }

    const rows = Object.keys(byStudent).map((id) => byStudent[id]);
    for (const row of rows) {
      row.attended = row.cells.filter((c) => c === 1).length;
      row.percent = held ? Math.round((100 * row.attended) / held) : 0;
    }
    rows.sort(
      (a, b) =>
        a.familyName.localeCompare(b.familyName) ||
        a.firstName.localeCompare(b.firstName)
    );

    // group rows by the student's path(s); a student in two paths appears in both groups
    const pathsByStudent: {
      [id: string]: { pathCode: number; pathName: string }[];
    } = {};
    for (const sp of studentPaths) {
      if (sp.pathCode === null || sp.pathCode === undefined) continue;
      if (!pathsByStudent[sp.studentID]) pathsByStudent[sp.studentID] = [];
      pathsByStudent[sp.studentID].push({
        pathCode: sp.pathCode,
        pathName: sp.pathName || `Path ${sp.pathCode}`,
      });
    }
    const groupMap: { [key: string]: PathGroup } = {};
    const addToGroup = (
      pathCode: number | null,
      pathName: string,
      order: number,
      row: MatrixRow
    ) => {
      const key = `${pathCode}|${order}`;
      if (!groupMap[key]) {
        groupMap[key] = {
          pathCode: pathCode,
          pathName: pathName,
          rows: [],
          included: true,
          order: order,
        };
      }
      groupMap[key].rows.push(row);
    };
    // only students of the path that was filtered; students of other
    // paths sharing this course cycle (and students with no path) are hidden.
    // within the path: first-timers (timesRepeated 0 or no record) before repeaters
    for (const row of rows) {
      const paths = pathsByStudent[row.studentID] || [];
      for (const p of paths) {
        if (p.pathCode === this.filteredPathCode) {
          // timesRepeated counts attempts in current data: 1 = first time,
          // 2+ = repeating (0 = legacy first-time rows, null = no record)
          const repeating = row.timesRepeated !== null && row.timesRepeated > 1;
          addToGroup(
            p.pathCode,
            `${p.pathName} - ${repeating ? "Repeating" : "First time"}`,
            repeating ? 1 : 0,
            row
          );
        }
      }
    }
    const groups = Object.keys(groupMap).map((k) => groupMap[k]);
    groups.sort((a, b) => a.order - b.order);
    let shownRows: MatrixRow[] = [];
    for (const g of groups) shownRows = shownRows.concat(g.rows);

    return {
      sessions: sessions,
      rows: shownRows,
      groups: groups,
      held: held,
      loading: false,
    };
  }

  exportCsv(cycle: AttendanceCycle) {
    const matrix = this.matrices[cycle.code];
    if (!matrix || matrix.loading) return;
    if (!this.hasIncludedGroup(matrix)) {
      this.showNotification("bottom", "left", 3, "No paths marked for export");
      return;
    }
    this.downloadCsv(
      [
        ...this.csvTitleLines(),
        [this.cycleTitle(cycle)],
        ...this.matrixCsvLines(matrix),
      ],
      `attendance_${this.sanitizeFileName(
        cycle.courseName || "cycle"
      )}_${cycle.code}.csv`
    );
  }

  teacherName(cycle: AttendanceCycle): string {
    return `${cycle.teacherFirstName || ""} ${
      cycle.teacherFamilyName || ""
    }`.trim();
  }

  private cycleTitle(cycle: AttendanceCycle): string {
    const teacher = this.teacherName(cycle);
    return (
      `${cycle.courseName || ""} - cycle ${cycle.code}` +
      (teacher ? ` - Teacher: ${teacher}` : "")
    );
  }

  // first lines of every export: which path cycle + its action number
  private csvTitleLines(): string[][] {
    if (!this.filteredPathCycle) return [];
    const line = [
      `Path cycle: ${
        this.filteredPathCycle.cycleName ||
        "Cycle " + this.filteredPathCycle.cycleId
      }`,
    ];
    if (this.filteredPathCycle.action_number != null) {
      line.push(`Action number: ${this.filteredPathCycle.action_number}`);
    }
    return [line, [""]];
  }

  // export a single path group of the matrix
  exportGroupCsv(cycle: AttendanceCycle, group: PathGroup) {
    const matrix = this.matrices[cycle.code];
    if (!matrix || matrix.loading) return;
    const lines: string[][] = [
      ...this.csvTitleLines(),
      [`${this.cycleTitle(cycle)} - ${group.pathName}`],
      ...this.csvHeaderRows(matrix),
    ];
    for (const row of group.rows) {
      lines.push(this.csvRowLine(row, matrix));
    }
    this.downloadCsv(
      lines,
      `attendance_${this.sanitizeFileName(cycle.courseName || "cycle")}_${
        cycle.code
      }_${this.sanitizeFileName(group.pathName)}.csv`
    );
  }

  // keep letters/digits and Hebrew (U+0590-U+05FF) in file names
  private sanitizeFileName(name: string): string {
    return name.replace(/[^\w֐-׿-]+/g, "_");
  }

  // one file, one section per cycle; loads any matrix not fetched yet
  exportAllCsv() {
    if (!this.cycles.length || this.exportingAll) return;
    this.exportingAll = true;
    const requests = this.cycles.map((cycle) => {
      const m = this.matrices[cycle.code];
      if (m && !m.loading) return of(m);
      return this.ServerApiService.fetchAttendanceMatrix(cycle.code).pipe(
        map((data: AttendanceMatrixData) => {
          const built = this.buildMatrix(data);
          this.matrices[cycle.code] = built; // keep it, so expanding later is instant
          return built;
        })
      );
    });
    forkJoin(requests).subscribe(
      (matrices: CycleMatrix[]) => {
        this.exportingAll = false;
        const lines: string[][] = [...this.csvTitleLines()];
        this.cycles.forEach((cycle, i) => {
          // skip cycles whose paths were all unmarked
          if (!matrices[i] || !this.hasIncludedGroup(matrices[i])) return;
          lines.push([this.cycleTitle(cycle)]);
          lines.push(...this.matrixCsvLines(matrices[i]));
          lines.push([""]);
        });
        if (!lines.length) {
          this.showNotification("bottom", "left", 3, "No paths marked for export");
          return;
        }
        this.downloadCsv(
          lines,
          `attendance_all_${this.formatDateParam(new Date())}.csv`
        );
      },
      (error) => {
        this.exportingAll = false;
        this.showNotification("bottom", "left", 4, "Could not export all cycles");
      }
    );
  }

  // two header rows: lesson topics above, session number + date below, same columns
  private csvHeaderRows(matrix: CycleMatrix): string[][] {
    const headerRow = [
      "Student",
      ...matrix.sessions.map(
        (s) => `S${s.session_num} ${this.formatDateDisplay(s.sessionDate)}`
      ),
      "Total",
      "%",
    ];
    const hasTopics = matrix.sessions.some((s) => !!s.lessonTopic);
    if (!hasTopics) return [headerRow];
    const topicsRow = [
      "Lesson topic",
      ...matrix.sessions.map((s) => s.lessonTopic || ""),
      "",
      "",
    ];
    return [topicsRow, headerRow];
  }

  private csvRowLine(row: MatrixRow, matrix: CycleMatrix): string[] {
    return [
      `${row.firstName} ${row.familyName}`.trim(),
      ...row.cells.map((c) => (c === null ? "" : String(c))),
      `${row.attended}/${matrix.held}`,
      `${row.percent}%`,
    ];
  }

  // only groups whose checkbox is still marked are exported
  private matrixCsvLines(matrix: CycleMatrix): string[][] {
    const lines: string[][] = [...this.csvHeaderRows(matrix)];
    for (const group of matrix.groups) {
      if (!group.included) continue;
      lines.push([group.pathName]);
      for (const row of group.rows) {
        lines.push(this.csvRowLine(row, matrix));
      }
    }
    return lines;
  }

  private hasIncludedGroup(matrix: CycleMatrix): boolean {
    return matrix.groups.some((g) => g.included);
  }

  private downloadCsv(lines: string[][], fileName: string) {
    const csv = lines
      .map((fields) => fields.map((f) => this.csvQuote(f)).join(","))
      .join("\r\n");
    // ﻿ = UTF-8 BOM so Excel renders Hebrew correctly
    const blob = new Blob(["﻿" + csv], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  }

  private csvQuote(value: string): string {
    return `"${String(value).replace(/"/g, '""')}"`;
  }

  formatDateDisplay(date: any): string {
    if (!date) return "";
    const d = new Date(date);
    return `${this.padTo2Digits(d.getDate())}/${this.padTo2Digits(
      d.getMonth() + 1
    )}/${d.getFullYear()}`;
  }

  private formatDateParam(date: Date): string {
    return [
      date.getFullYear(),
      this.padTo2Digits(date.getMonth() + 1),
      this.padTo2Digits(date.getDate()),
    ].join("-");
  }

  private padTo2Digits(num: number): string {
    return num.toString().padStart(2, "0");
  }

  showNotification(from, align, t: number, mess: string) {
    const type = ["", "info", "success", "warning", "danger"];
    $.notify(
      {
        icon: "notifications",
        message: mess,
      },
      {
        type: type[t],
        timer: 4000,
        placement: {
          from: from,
          align: align,
        },
        template:
          '<div data-notify="container" class="col-xl-4 col-lg-4 col-11 col-sm-4 col-md-4 alert alert-{0} alert-with-icon" role="alert">' +
          '<button mat-button  type="button" aria-hidden="true" class="close mat-button" data-notify="dismiss">  <i class="material-icons">close</i></button>' +
          '<i class="material-icons" data-notify="icon">notifications</i> ' +
          '<span data-notify="title">{1}</span> ' +
          '<span data-notify="message">{2}</span>' +
          '<div class="progress" data-notify="progressbar">' +
          '<div class="progress-bar progress-bar-{0}" role="progressbar" aria-valuenow="0" aria-valuemin="0" aria-valuemax="100" style="width: 0%;"></div>' +
          "</div>" +
          '<a href="{3}" target="{4}" data-notify="url"></a>' +
          "</div>",
      }
    );
  }
}
